// Timezone-aware date formatting for the AI-matching views, using the native Intl API (no extra
// date library). Times come from the API as UTC ISO strings. The event carries a display mode:
// Static (pin to the event's IANA timezone) or Dynamic (each viewer's local timezone).
//
// AgendaTimezoneMode enum from the backend: Dynamic = 1, Static = 2 (serialized numerically).

export const TIMEZONE_MODE_STATIC = 2;

export function isStaticMode(mode) {
  return mode === TIMEZONE_MODE_STATIC || mode === 'Static';
}

// The IANA zone to display in for this event + mode. Static → the event zone; otherwise undefined
// so Intl falls back to the viewer's local zone.
function displayZone(eventTz, mode) {
  return isStaticMode(mode) && eventTz ? eventTz : undefined;
}

// Format a UTC ISO timestamp with the given Intl.DateTimeFormat options, in the event's display
// zone. Returns '' for empty/invalid input.
export function formatEventDateTime(utcIso, eventTz, mode, options, locale) {
  if (!utcIso) return '';
  const date = new Date(utcIso);
  if (Number.isNaN(date.getTime())) return '';
  const zone = displayZone(eventTz, mode);
  return new Intl.DateTimeFormat(locale || 'en-GB', {
    ...options,
    ...(zone ? { timeZone: zone } : {})
  }).format(date);
}

// Stable yyyy-MM-dd key for the date in the display zone (en-CA renders ISO-style). Used for
// grouping slots/sessions by day.
export function eventDateKey(utcIso, eventTz, mode) {
  if (!utcIso) return '';
  const date = new Date(utcIso);
  if (Number.isNaN(date.getTime())) return '';
  const zone = displayZone(eventTz, mode);
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(zone ? { timeZone: zone } : {})
  }).format(date);
}

// Offset (ms) of an IANA zone at a given UTC instant. Used to convert a wall-clock time entered in
// the event zone back to UTC.
function zoneOffsetMillis(timeZone, utcMillis) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = {};
  dtf.formatToParts(new Date(utcMillis)).forEach((p) => {
    parts[p.type] = p.value;
  });
  const hour = parts.hour === '24' ? 0 : Number(parts.hour);
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return asIfUtc - utcMillis;
}

// Convert a datetime-local value ("yyyy-MM-ddTHH:mm", a wall-clock time) into a UTC ISO string.
// In Static mode the wall time is interpreted in the event zone; otherwise the viewer's local zone.
export function composeEventIso(localValue, eventTz, mode) {
  if (!localValue) return null;
  const [datePart, timePart] = localValue.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);

  const zone = displayZone(eventTz, mode);
  if (!zone) {
    // Local interpretation: the platform parses this as local time.
    const local = new Date(y, m - 1, d, hh, mm);
    return Number.isNaN(local.getTime()) ? null : local.toISOString();
  }

  // Treat the wall time as if it were UTC, then subtract the event zone's offset at that instant.
  const asUtc = Date.UTC(y, m - 1, d, hh, mm);
  const offset = zoneOffsetMillis(zone, asUtc);
  return new Date(asUtc - offset).toISOString();
}
